// Shared components for Purely marketing site.
// Exposes globals: Nav, Footer, ScoreRing, IngredientRow, useTweaks helpers, applyTweaks.

const PURELY_APP_STORE_URL = 'https://apps.apple.com/us/app/purely-clean-beauty-scanner/id6761502791';

const TweakDefaults = {
  // Brand
  "primaryColor": "#7C9A82",
  "darkForest": "#2D3B30",
  "bgStyle": "warm",          // warm | cream | forest
  "accentWordmark": "forest", // forest | sage
  "contrast": "normal",       // normal | high

  // Typography
  "displayFont": "Instrument Serif",  // Instrument Serif | Cormorant Garamond | EB Garamond | DM Serif Display
  "bodyFont": "Mona Sans",            // Mona Sans | Inter | Manrope | system

  // Layout
  "density": "regular",       // compact | regular | airy
  "ctaShape": "pill",         // pill | rounded
  "ctaStyle": "sage",         // sage | outline | forest
  "ringShadows": true,
  "stickyNav": true,

  // Hero
  "heroVisual": "score",      // score | phone | minimal
  "heroAlign": "left",        // left | center
  "heroHeadline": "Know what's in your skincare.",
  "heroSubheadline": "Scan skincare fast, decode ingredients, and spot flagged ingredients so you can compare cleaner options and make informed choices.",
  "heroCta": "Download for iOS",

  // Score
  "scoreNumber": 84,
  "scoreLabel": "Ingredient Clarity",

  // Signal bar
  "showSignalBar": true,

  // Cards / steps
  "stepNumberStyle": "watermark", // watermark | inline | hidden
  "showStepIcons": true,

  // Demo
  "demoBg": "warm",            // warm | cream

  // Waitlist
  "waitlistEmphasis": "calm",  // calm | bold
};

// Font import map (loaded on demand into <link> tag).
// Instrument Serif / Mona Sans are the site's shipped defaults and are
// self-hosted via styles/fonts.css (GH-892) — no network fetch needed, so
// they map to null like "system". The rest are tweaks-panel preview-only
// alternates, never used in production copy, and stay on Google Fonts.
const FontMap = {
  "Instrument Serif": null,
  "Cormorant Garamond": "https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@1,500&display=swap",
  "EB Garamond": "https://fonts.googleapis.com/css2?family=EB+Garamond:ital,wght@1,500&display=swap",
  "DM Serif Display": "https://fonts.googleapis.com/css2?family=DM+Serif+Display:ital@0;1&display=swap",
  "Mona Sans": null,
  "Inter": "https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap",
  "Manrope": "https://fonts.googleapis.com/css2?family=Manrope:wght@400;500;600;700&display=swap",
  "system": null,
};
function ensureFont(family) {
  if (!family || !FontMap[family]) return;
  const id = `font-${family.replace(/\s+/g, '-')}`;
  if (document.getElementById(id)) return;
  const link = document.createElement('link');
  link.id = id;
  link.rel = 'stylesheet';
  link.href = FontMap[family];
  document.head.appendChild(link);
}

function applyTweaks(t) {
  const root = document.documentElement;
  // CSS vars
  root.style.setProperty('--p-sage-green', t.primaryColor);
  root.style.setProperty('--p-dark-forest', t.darkForest);

  // fonts
  ensureFont(t.displayFont);
  ensureFont(t.bodyFont);
  const serifStack = `'${t.displayFont}', 'Times New Roman', Georgia, serif`;
  const sansStack = t.bodyFont === 'system'
    ? `-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'Segoe UI', Roboto, sans-serif`
    : `'${t.bodyFont}', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif`;
  root.style.setProperty('--font-serif', serifStack);
  root.style.setProperty('--font-sans', sansStack);

  // body classes / data attrs
  document.body.dataset.bgStyle = t.bgStyle;
  document.body.dataset.accent = t.accentWordmark;
  document.body.dataset.contrast = t.contrast;
  document.body.dataset.density = t.density;
  document.body.dataset.ctaShape = t.ctaShape;
  document.body.dataset.ctaStyle = t.ctaStyle;
  document.body.dataset.heroVisual = t.heroVisual;
  document.body.dataset.heroAlign = t.heroAlign;
  document.body.dataset.demoBg = t.demoBg;
  document.body.dataset.stepNumberStyle = t.stepNumberStyle;
  document.body.dataset.showStepIcons = t.showStepIcons ? '1' : '0';
  document.body.dataset.ringShadows = t.ringShadows ? '1' : '0';
  document.body.dataset.stickyNav = t.stickyNav ? '1' : '0';
  document.body.dataset.waitlistEmphasis = t.waitlistEmphasis;
  document.body.dataset.showSignalBar = t.showSignalBar ? '1' : '0';

  // Cta shape
  const ctaRadius = t.ctaShape === 'rounded' ? '12px' : '999px';
  document.querySelectorAll('.btn').forEach(b => b.style.borderRadius = ctaRadius);

  // Sticky nav
  document.querySelectorAll('.nav').forEach(n => {
    n.style.position = t.stickyNav ? 'sticky' : 'static';
  });

  // CTA style override
  document.querySelectorAll('.btn--primary').forEach(b => {
    if (t.ctaStyle === 'outline') {
      b.style.background = 'transparent';
      b.style.color = 'var(--p-dark-forest)';
      b.style.border = `1.5px solid var(--p-dark-forest)`;
      b.style.boxShadow = 'none';
    } else if (t.ctaStyle === 'forest') {
      b.style.background = 'var(--p-dark-forest)';
      b.style.color = '#fff';
      b.style.border = 'none';
      b.style.boxShadow = '0 4px 14px 0 rgba(45,59,48,0.25)';
    } else {
      b.style.background = 'var(--p-sage-green)';
      b.style.color = '#fff';
      b.style.border = 'none';
      b.style.boxShadow = '0 4px 14px 0 color-mix(in srgb, var(--p-sage-green) 32%, transparent)';
    }
  });

  // Ring shadows toggle
  if (!t.ringShadows) {
    const style = document.getElementById('ring-shadow-override') || document.createElement('style');
    style.id = 'ring-shadow-override';
    style.textContent = `
      .step-card, .demo__card, .score-card, .long-card, .band, .faq__item, .annex {
        box-shadow: none !important;
      }
    `;
    document.head.appendChild(style);
  } else {
    const s = document.getElementById('ring-shadow-override');
    if (s) s.remove();
  }

  // Signal bar visibility
  document.querySelectorAll('.signal').forEach(s => {
    s.style.display = t.showSignalBar ? '' : 'none';
  });
}

// ─── Nav ────────────────────────────────────────────────────────────────
function Nav({ active }) {
  return (
    <nav className="nav">
      <div className="container nav__inner">
        <a href="index.html" className="wordmark" aria-label="Purely">Purely</a>
        <div className="nav__links">
          <a href="how-it-works.html" className={`nav__link ${active === 'how' ? 'active' : ''}`}>How It Works</a>
          <a href="privacy.html" className={`nav__link ${active === 'privacy' ? 'active' : ''}`}>Privacy</a>
          <a href="support.html" className={`nav__link ${active === 'support' ? 'active' : ''}`}>Support</a>
          <a href={PURELY_APP_STORE_URL} className="btn btn--primary btn--sm hide-mobile-btn">Download</a>
        </div>
      </div>
    </nav>
  );
}

// ─── Footer ─────────────────────────────────────────────────────────────
function Footer() {
  return (
    <footer className="footer">
      <div className="container">
        <div className="footer__top">
          <a href="index.html" className="wordmark" aria-label="Purely">Purely</a>
          <div className="footer__links">
            <a href="privacy.html">Privacy Policy</a>
            <a href="terms/index.html">Terms of Use</a>
            <a href="how-it-works.html">How It Works</a>
            <a href="support.html">Support</a>
            <a href={PURELY_APP_STORE_URL}>Download the App</a>
            <a href="index.html#waitlist">Email Updates</a>
          </div>
        </div>
        <p className="footer__legal">
          Purely is not a medical device. Ingredient Clarity is informational for clearer skin choices — EU-inspired cleanliness, not legal advice. Always consult a licensed dermatologist for medical concerns.
        </p>
        <p className="footer__copy">© 2026 Purely. All rights reserved.</p>
      </div>
    </footer>
  );
}

// ─── Score Ring SVG ─────────────────────────────────────────────────────
function ScoreRing({ value = 84, label = "Ingredient Clarity" }) {
  const R = 92;
  const C = 2 * Math.PI * R;
  const pct = Math.max(0, Math.min(100, value));
  const dash = (pct / 100) * C;
  const tone = pct >= 80 ? 'green' : pct >= 50 ? 'yellow' : 'red';
  const trackColor = 'var(--border-subtle)';
  const fillColor = tone === 'green' ? 'var(--p-pastel-green)'
    : tone === 'yellow' ? 'var(--p-pastel-yellow)' : 'var(--p-pastel-red)';
  const numColor = tone === 'green' ? 'var(--p-score-green)'
    : tone === 'yellow' ? 'var(--p-score-yellow)' : 'var(--p-score-red)';
  return (
    <div className="score-card" data-reveal>
      <div className="score-card__topline" data-reveal="fade" data-reveal-delay="150">
        <span><span className="score-card__dot"></span>Live scan · Demo</span>
        <span style={{fontFamily: 'var(--font-mono)', textTransform: 'none', letterSpacing: 0}}>
          5901234123457
        </span>
      </div>
      <div className="score-ring-wrap" data-reveal="scale" data-reveal-delay="250">
        <div className="score-ring">
          <svg viewBox="0 0 220 220" aria-hidden="true">
            <circle cx="110" cy="110" r={R} fill="none" stroke={trackColor} strokeWidth="14" />
            <circle cx="110" cy="110" r={R} fill="none" stroke={fillColor} strokeWidth="14"
              strokeLinecap="round"
              strokeDasharray={`${dash} ${C - dash}`}
              data-ring-fill={pct} />
          </svg>
          <div className="score-ring__inner">
            <span className="score-ring__num" style={{color: numColor}} data-count-to={pct} data-count-duration="1600">{pct}</span>
            <span className="score-ring__denom"> 100 · {label}</span>
          </div>
        </div>
      </div>
      <div className="score-card__rows">
        <div className="score-card__row" data-reveal="left" data-reveal-delay="600">
          <span className="chip chip--approved">Clear</span>
          <span className="name">aqua</span>
          <span className="meta">—</span>
        </div>
        <div className="score-card__row" data-reveal="left" data-reveal-delay="720">
          <span className="chip chip--regulated">Flagged</span>
          <span className="name">salicylic acid</span>
          <span className="meta">Ingredient reference</span>
        </div>
        <div className="score-card__row" data-reveal="left" data-reveal-delay="840">
          <span className="chip chip--approved">Clear</span>
          <span className="name">glycerin</span>
          <span className="meta">—</span>
        </div>
      </div>
    </div>
  );
}

// ─── Phone visual (alternate hero) ──────────────────────────────────────
// TODO: Replace this hand-built mockup with a real screenshot of the iOS app
// before launch. This is a placeholder approximation — fine for waitlist phase,
// but the marketing site should ship with the actual App Store screenshot.
function PhoneMockup() {
  return (
    <div className="phone-mockup">
      <div className="phone-mockup__screen" style={{ padding: 0 }}>
        <img src="assets/phone-result.png" alt="Purely App Screenshot" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
      </div>
    </div>
  );
}

// ─── Minimal hero visual ───────────────────────────────────────────────
function MinimalScore({ score = 84 }) {
  const tone = score >= 80 ? 'var(--p-pastel-green)' : score >= 50 ? 'var(--p-pastel-yellow)' : 'var(--p-pastel-red)';
  const numColor = score >= 80 ? 'var(--p-score-green)' : score >= 50 ? 'var(--p-score-yellow)' : 'var(--p-score-red)';
  const C = 2 * Math.PI * 92;
  return (
    <div style={{position: 'relative', width: 320, height: 320}}>
      <svg viewBox="0 0 220 220" style={{width: '100%', height: '100%', transform: 'rotate(-90deg)'}}>
        <circle cx="110" cy="110" r="92" fill="none" stroke="var(--border-subtle)" strokeWidth="2" />
        <circle cx="110" cy="110" r="92" fill="none" stroke={tone} strokeWidth="2"
          strokeLinecap="round"
          strokeDasharray={`${(score/100) * C} ${C}`} />
      </svg>
      <div style={{
        position: 'absolute', inset: 0,
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
      }}>
        <span style={{
          fontFamily: 'var(--font-serif)', fontStyle: 'italic',
          fontWeight: 500, fontSize: 130, lineHeight: 1,
          color: numColor, fontVariantNumeric: 'tabular-nums',
          letterSpacing: '-0.02em',
        }}>{score}</span>
        <span style={{
          fontFamily: 'var(--font-sans)', fontSize: 13,
          color: 'var(--fg-secondary)', marginTop: 14,
          letterSpacing: '0.04em',
        }}>/ 100 · Ingredient Clarity</span>
      </div>
    </div>
  );
}

// ─── Floating Particles ─────────────────────────────────────────────────
function FloatingParticles({ count = 12 }) {
  const INGREDIENT_NAMES = [
    'Niacinamide', 'Retinol', 'Aqua', 'Glycerin', 'Squalane',
    'Ceramide', 'Peptide', 'Collagen', 'Hyaluronic', 'Tocopherol',
    'Allantoin', 'Bisabolol', 'Centella', 'Adenosine', 'Panthenol',
  ];

  const particles = React.useMemo(() => {
    return Array.from({ length: count }, (_, i) => ({
      text: INGREDIENT_NAMES[i % INGREDIENT_NAMES.length],
      left: 5 + Math.random() * 90,
      size: 10 + Math.random() * 5,
      opacity: 0.06 + Math.random() * 0.08,
      duration: 18 + Math.random() * 14,
      delay: Math.random() * -20,
      swayDuration: 4 + Math.random() * 3,
      color: i % 3 === 0 ? 'var(--p-sage-green)' : i % 3 === 1 ? 'var(--p-pastel-red)' : 'var(--p-medium-sage)',
    }));
  }, [count]);

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none" style={{ zIndex: 1 }}>
      {particles.map((p, i) => (
        <span
          key={i}
          className="floating-particle"
          style={{
            left: `${p.left}%`,
            bottom: '-5%',
            fontSize: `${p.size}px`,
            color: p.color,
            opacity: 0,
            animation: `floatUp ${p.duration}s linear ${p.delay}s infinite`,
          }}
        >
          <span
            style={{
              display: 'inline-block',
              animation: `floatSway ${p.swayDuration}s ease-in-out infinite`,
            }}
          >
            {p.text}
          </span>
        </span>
      ))}
    </div>
  );
}

// ─── Scanner Demo ───────────────────────────────────────────────────────
function ScannerDemo() {
  const [scanning, setScanning] = React.useState(false);
  const [showResult, setShowResult] = React.useState(false);
  const [scanProgress, setScanProgress] = React.useState(0);
  const [displayScore, setDisplayScore] = React.useState(0);
  const resultRef = React.useRef(null);

  const demoIngredients = [
    { name: 'Aqua', status: 'Clear', description: 'Water. Universal solvent and base for the formula.', regulation: 'No meaningful flags' },
    { name: 'Niacinamide', status: 'Clear', description: 'Vitamin B3 at 10%. Often used for tone and barrier support.', regulation: 'No meaningful flags' },
    { name: 'Pentylene Glycol', status: 'Clear', description: 'Humectant and solvent used in cosmetic formulas.', regulation: 'No meaningful flags' },
    { name: 'Zinc PCA', status: 'Clear', description: 'Zinc salt of pyrrolidone carboxylic acid, often used in targeted formulas.', regulation: 'No meaningful flags' },
    { name: 'Dimethyl Isosorbide', status: 'Clear', description: 'Solubilizer used to help dissolve active ingredients.', regulation: 'No meaningful flags' },
    { name: 'Xanthan Gum', status: 'Clear', description: 'Polysaccharide thickener and emulsion stabilizer.', regulation: 'No meaningful flags' },
    { name: 'Phenoxyethanol', status: 'Flagged', description: 'Preservative widely used in cosmetics. Check leave-on use conditions.', regulation: 'Ingredient reference · worth noticing' },
    { name: 'Chlorphenesin', status: 'Flagged', description: 'Preservative and antifungal agent. Concentration limits apply.', regulation: 'Ingredient reference · worth noticing' },
  ];
  const demoIngredientCount = demoIngredients.length;
  const demoFlaggedCount = demoIngredients.filter((ingredient) => ingredient.status === 'Flagged' || ingredient.status === 'Prefer to avoid').length;

  const clarityScore = 92;

  function getScoreColor(score) {
    if (score >= 80) return 'var(--p-pastel-green)';
    if (score >= 50) return 'var(--p-pastel-yellow)';
    return 'var(--p-dark-forest)';
  }

  function getPillStyle(status) {
    switch (status) {
      case 'Prefer to avoid': return { background: 'var(--p-dark-forest)', color: '#fff' };
      case 'Flagged': return { background: 'var(--p-pastel-yellow)', color: 'var(--p-dark-forest)' };

      case 'Clear': return { background: 'var(--p-pastel-green)', color: 'var(--p-dark-forest)' };
      default: return { background: 'color-mix(in srgb, var(--p-sage-green) 20%, transparent)', color: 'var(--p-medium-sage)' };
    }
  }

  function animateScore(target) {
    let current = 0;
    const step = target / 40;
    function tick() {
      current += step;
      if (current >= target) {
        setDisplayScore(target);
        return;
      }
      setDisplayScore(Math.round(current));
      requestAnimationFrame(tick);
    }
    requestAnimationFrame(tick);
  }

  function handleScan() {
    if (scanning || showResult) {
      setShowResult(false);
      setScanning(false);
      setScanProgress(0);
      setDisplayScore(0);
      return;
    }
    setScanning(true);
    setScanProgress(0);

    const duration = 2000;
    const start = performance.now();
    function tick() {
      const elapsed = performance.now() - start;
      const p = Math.min(elapsed / duration, 1);
      setScanProgress(p);
      if (p < 1) {
        requestAnimationFrame(tick);
      } else {
        setScanning(false);
        setShowResult(true);
        animateScore(clarityScore);
      }
    }
    requestAnimationFrame(tick);
  }

  React.useEffect(() => {
    if (!showResult || !resultRef.current) return;
    const el = resultRef.current;
    el.style.opacity = '0';
    el.style.transform = 'translateY(24px) scale(0.95)';
    requestAnimationFrame(() => {
      el.style.transition = 'opacity 0.7s cubic-bezier(0.16, 1, 0.3, 1), transform 0.7s cubic-bezier(0.16, 1, 0.3, 1)';
      el.style.opacity = '1';
      el.style.transform = 'translateY(0) scale(1)';
    });
  }, [showResult]);

  const IconCamera = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/></svg>
  );
  const IconX = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
  );
  const IconScanLine = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="6" x2="3" y2="6"/><line x1="3" y1="18" x2="3" y2="18"/></svg>
  );

  return (
    <section id="scanner" className="section-scanner" data-reveal>
      <div className="container" style={{ paddingTop: 96, paddingBottom: 120 }}>
        <div style={{ textAlign: 'center', maxWidth: 560, margin: '0 auto' }}>
          <span className="overline" data-reveal>Interactive Demo</span>
          <h2 className="h-section" data-reveal data-reveal-delay="120">See How Purely Works</h2>
          <p className="lede" style={{ margin: '16px auto 0' }} data-reveal data-reveal-delay="220">
            Click the camera button to scan a product label. Watch as Purely decodes the ingredient list and highlights regulatory flags.
          </p>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginTop: 64 }}>
          {/* Camera viewport */}
          <div className="scanner-viewport">
            <div style={{
              width: '100%', height: '100%',
              background: 'linear-gradient(135deg, #E8E4DE 0%, #DDD8D0 50%, #D5CFC5 100%)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <img
                src="assets/product-niacinamide.png"
                alt="The Ordinary Niacinamide 10% + Zinc 1%"
                style={{ width: '88%', height: 'auto', objectFit: 'contain', filter: 'drop-shadow(0 8px 24px rgba(45,59,48,0.15))' }}
              />
            </div>

            {/* Scan line */}
            {scanning && <div className="scan-line" />}

            {/* Corner brackets */}
            <div className="scanner-corner scanner-corner--tl" style={{ animation: `cornerFlash 2s ease-in-out 0s infinite` }} />
            <div className="scanner-corner scanner-corner--tr" style={{ animation: `cornerFlash 2s ease-in-out 0.3s infinite` }} />
            <div className="scanner-corner scanner-corner--bl" style={{ animation: `cornerFlash 2s ease-in-out 0.6s infinite` }} />
            <div className="scanner-corner scanner-corner--br" style={{ animation: `cornerFlash 2s ease-in-out 0.9s infinite` }} />

            {/* Scanning overlay */}
            {scanning && (
              <div style={{
                position: 'absolute', inset: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                pointerEvents: 'none', zIndex: 30,
              }}>
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '10px 20px', borderRadius: 999,
                  background: 'rgba(45, 59, 48, 0.7)',
                  backdropFilter: 'blur(4px)',
                  color: '#fff', fontSize: 14, fontFamily: 'var(--font-sans)',
                }}>
                  <span style={{ animation: 'gentleSpin 1s linear infinite', display: 'inline-flex' }}><IconScanLine /></span>
                  Scanning&hellip; {Math.round(scanProgress * 100)}%
                </div>
              </div>
            )}
          </div>

          {/* Scan button */}
          <button
            onClick={handleScan}
            className={`scan-button ${!scanning && !showResult ? 'scan-button--idle' : ''}`}
            aria-label={scanning ? 'Scanning' : showResult ? 'Reset' : 'Start scan'}
          >
            <span style={{ color: '#fff' }}>
              {showResult ? <IconX /> : <IconCamera />}
            </span>
          </button>
        </div>

        {/* Result card */}
        {showResult && (
          <div ref={resultRef} className="scanner-result" style={{ margin: '32px auto 0' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20 }}>
              <div>
                <h3 className="h-card">The Ordinary Niacinamide 10% + Zinc 1%</h3>
                <p style={{ fontSize: 14, color: 'var(--fg-secondary)', marginTop: 4 }}>
                  {demoIngredientCount} ingredients analyzed · {demoFlaggedCount} flagged
                </p>
              </div>
              <div
                className="score-circle-enter score-pulse"
                style={{
                  width: 56, height: 56, borderRadius: '50%',
                  background: getScoreColor(displayScore),
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <span style={{ fontFamily: 'var(--font-sans)', fontSize: 20, fontWeight: 700, color: '#fff' }}>
                  {displayScore}
                </span>
              </div>
            </div>

            <div className="scanner-result__score-bar">
              <div className="scanner-result__score-fill" style={{ width: `${displayScore}%` }} />
            </div>

            <div style={{ marginTop: 20, display: 'flex', flexDirection: 'column', gap: 12, maxHeight: 260, overflowY: 'auto' }}>
              {demoIngredients.map((ing, i) => (
                <div
                  key={i}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
                    opacity: 0,
                    animation: `ingredientStagger 0.4s ease ${i * 0.08}s forwards`,
                  }}
                >
                  <div style={{ minWidth: 0 }}>
                    <span style={{ fontFamily: 'var(--font-mono)', fontSize: 13, color: 'var(--fg-primary)' }}>{ing.name}</span>
                    <p style={{ fontSize: 12, color: 'var(--fg-secondary)', marginTop: 2 }}>{ing.description}</p>
                  </div>
                  <span
                    className="chip"
                    style={{ ...getPillStyle(ing.status), flexShrink: 0 }}
                  >
                    {ing.status}
                  </span>
                </div>
              ))}
            </div>

            <p style={{ fontSize: 12, color: 'var(--fg-secondary)', marginTop: 16, fontStyle: 'italic', lineHeight: 1.5 }}>
              *This is a demonstration. Ingredient Clarity is informational — not medical or legal advice.
            </p>
          </div>
        )}
      </div>
    </section>
  );
}

// ─── Ingredients Explorer ───────────────────────────────────────────────
function IngredientsExplorer() {
  const [activeFilter, setActiveFilter] = React.useState('All');
  const [searchQuery, setSearchQuery] = React.useState('');
  const [visibleCount, setVisibleCount] = React.useState(6);

  const filters = ['All', 'Clear', 'Flagged', 'Prefer to avoid'];

  const allIngredients = [
    { name: 'Aqua', inci: 'Water', status: 'Clear', description: 'Universal solvent and base for most cosmetic formulations.', regulation: 'No meaningful flags' },
    { name: 'Glycerin', inci: 'Glycerol', status: 'Clear', description: 'Humectant used to help formulas retain moisture.', regulation: 'No meaningful flags' },
    { name: 'Niacinamide', inci: 'Nicotinamide', status: 'Clear', description: 'Vitamin B3 ingredient used in many leave-on formulas.', regulation: 'No meaningful flags' },
    { name: 'Sodium Hyaluronate', inci: 'Hyaluronic acid sodium salt', status: 'Clear', description: 'Humectant used for lightweight hydration feel.', regulation: 'No meaningful flags' },
    { name: 'Phenoxyethanol', inci: '2-Phenoxyethanol', status: 'Flagged', description: 'Widely used preservative. Check leave-on use conditions in source details.', regulation: 'Ingredient reference · worth noticing' },
    { name: 'Butylparaben', inci: 'Butyl p-hydroxybenzoate', status: 'Prefer to avoid', description: 'Long-chain paraben. Stronger caution for cleaner-skin goals — review source details.', regulation: 'Ingredient reference · stronger caution' },
    { name: 'Formaldehyde', inci: 'Methanal', status: 'Prefer to avoid', description: 'Preservative and hardening agent. Stronger caution for cleaner-skin goals — review source details.', regulation: 'Ingredient reference · stronger caution' },
    { name: 'Hydroquinone', inci: '1,4-Benzenediol', status: 'Prefer to avoid', description: 'Skin-lightening agent. Stronger caution for cleaner-skin goals — review source details.', regulation: 'Ingredient reference · stronger caution' },
    { name: 'Retinol', inci: 'Vitamin A alcohol', status: 'Flagged', description: 'Anti-aging vitamin A derivative. Concentration limits apply.', regulation: 'Ingredient reference · worth noticing' },
    { name: 'Salicylic Acid', inci: '2-Hydroxybenzoic acid', status: 'Flagged', description: 'BHA exfoliant. Max 2% rinse-off, 0.5% leave-on.', regulation: 'Ingredient reference · worth noticing' },
    { name: 'Sodium Lauryl Sulfate', inci: 'SLS', status: 'Flagged', description: 'Foaming surfactant. Irritation potential at high concentrations.', regulation: 'Ingredient reference · worth noticing' },
    { name: 'Tocopherol', inci: 'Vitamin E', status: 'Clear', description: 'Antioxidant vitamin E ingredient used in many cosmetic formulas.', regulation: 'No meaningful flags' },
    { name: 'Zinc Oxide (Nano)', inci: 'CI 77947', status: 'Flagged', description: 'UV filter and soothing agent. Nano form may have specific use conditions.', regulation: 'Ingredient reference · worth noticing' },
    { name: 'Triclosan', inci: '5-Chloro-2-(2,4-dichlorophenoxy)phenol', status: 'Prefer to avoid', description: 'Antimicrobial agent with stronger caution for cleaner-skin goals.', regulation: 'Ingredient reference · stronger caution' },
    { name: 'Centella Asiatica Extract', inci: 'Gotu Kola', status: 'Clear', description: 'Botanical extract used in many cosmetic formulas.', regulation: 'No meaningful flags' },
    { name: 'Propylparaben', inci: 'Propyl p-hydroxybenzoate', status: 'Prefer to avoid', description: 'Medium-chain paraben. Max 0.14% in finished products.', regulation: 'Ingredient reference · stronger caution' },
    { name: 'Ethylhexyl Methoxycinnamate', inci: 'Octinoxate', status: 'Flagged', description: 'UVB filter. Review source details for use conditions.', regulation: 'Ingredient reference · worth noticing' },
    { name: 'Alpha-Arbutin', inci: '4-Hydroxyphenyl-alpha-D-glucopyranoside', status: 'Clear', description: 'Skin-brightening ingredient often compared with hydroquinone.', regulation: 'No meaningful flags' },
  ];

  function getPillStyle(status) {
    switch (status) {
      case 'Prefer to avoid': return { background: 'var(--p-dark-forest)', color: '#fff' };
      case 'Flagged': return { background: 'var(--p-pastel-yellow)', color: 'var(--p-dark-forest)' };

      case 'Clear': return { background: 'var(--p-pastel-green)', color: 'var(--p-dark-forest)' };
      default: return { background: 'color-mix(in srgb, var(--p-sage-green) 20%, transparent)', color: 'var(--p-medium-sage)' };
    }
  }

  const filtered = allIngredients.filter((ing) => {
    const matchesFilter = activeFilter === 'All' || ing.status === activeFilter;
    const matchesSearch = searchQuery === '' ||
      ing.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      ing.inci.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  const visible = filtered.slice(0, visibleCount);

  const IconSearch = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
  );
  const IconSliders = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="4" y1="21" x2="4" y2="14"/><line x1="4" y1="10" x2="4" y2="3"/><line x1="12" y1="21" x2="12" y2="12"/><line x1="12" y1="8" x2="12" y2="3"/><line x1="20" y1="21" x2="20" y2="16"/><line x1="20" y1="12" x2="20" y2="3"/><line x1="1" y1="14" x2="7" y2="14"/><line x1="9" y1="8" x2="15" y2="8"/><line x1="17" y1="16" x2="23" y2="16"/></svg>
  );

  return (
    <section id="ingredients" className="section-explorer" style={{ padding: '120px 0' }}>
      <FloatingParticles count={10} />

      <div className="container" style={{ position: 'relative', zIndex: 2 }}>
        <div style={{ textAlign: 'center', maxWidth: 600, margin: '0 auto' }}>
          <span className="overline" data-reveal>Ingredient Database</span>
          <h2 className="h-section" data-reveal data-reveal-delay="120">2,332+ Ingredients, One Database</h2>
          <p className="lede" style={{ margin: '16px auto 0' }} data-reveal data-reveal-delay="220">
            Decode ingredient lists, spot flagged ingredients, and compare cleaner options. Search by name or browse common skincare components to make more informed choices.
          </p>
        </div>

        {/* Search + Filter */}
        <div style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          gap: 16, marginTop: 40,
        }}>
          <div className="explorer-search">
            <span className="explorer-search__icon"><IconSearch /></span>
            <input
              type="text"
              placeholder="Search ingredients..."
              value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); setVisibleCount(6); }}
              className="explorer-search__input"
              aria-label="Search ingredients"
            />
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', justifyContent: 'center' }}>
            <span style={{ color: 'var(--fg-secondary)', display: 'inline-flex' }}><IconSliders /></span>
            {filters.map((f) => (
              <button
                key={f}
                onClick={() => { setActiveFilter(f); setVisibleCount(6); }}
                className={`explorer-filter ${activeFilter === f ? 'explorer-filter--active' : ''}`}
                aria-pressed={activeFilter === f}
                aria-label={`Filter by ${f}`}
              >
                {f}
              </button>
            ))}
          </div>
        </div>

        {/* Ingredient grid */}
        <div className="explorer-grid" style={{ marginTop: 40 }}>
          {visible.map((ing, i) => (
            <div
              key={`${ing.name}-${activeFilter}-${i}`}
              className="explorer-card magnetic-hover"
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
                <div style={{ minWidth: 0 }}>
                  <div className="explorer-card__name">{ing.name}</div>
                  <div className="explorer-card__inci">{ing.inci}</div>
                </div>
                <span
                  className="chip"
                  style={{ ...getPillStyle(ing.status), flexShrink: 0 }}
                >
                  {ing.status}
                </span>
              </div>
              <p className="explorer-card__desc">{ing.description}</p>
              <p className="explorer-card__regulation">{ing.regulation}</p>
            </div>
          ))}
        </div>

        {visibleCount < filtered.length && (
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: 32 }}>
            <button
              onClick={() => setVisibleCount((c) => c + 6)}
              className="load-more"
            >
              Load More
            </button>
          </div>
        )}

        {visible.length === 0 && (
          <div style={{ textAlign: 'center', padding: '64px 0' }}>
            <p style={{ fontSize: 16, color: 'var(--fg-secondary)' }}>No ingredients match your search.</p>
          </div>
        )}
      </div>
    </section>
  );
}

Object.assign(window, {
  TweakDefaults, applyTweaks, ensureFont,
  Nav, Footer, ScoreRing, PhoneMockup, MinimalScore,
  FloatingParticles, ScannerDemo, IngredientsExplorer,
  PURELY_APP_STORE_URL,
});
